"use client";
import React, { useState, useEffect, useMemo } from "react";
import { useTranslations } from "next-intl";
import { Drawer } from "antd";
import useIsMobile from "@/lib/hooks/useIsMobile";
import Categories from "@/components/ProductList/Categories";
import { SearchBar } from "../SearchBar/page";
import { useBreakpointColumns } from "@/lib/store/breakpointColumnsObj";
import { usePathname } from "@/navigation";
import CommonTaile from "../Common/CommonTaile";

// 递归查找分类描述的函数
const findCategoryDescription = (categories: any[], targetSlug: string): Object | null => {
	for (const category of categories) {
		if (category.node.slug === targetSlug) {
			return {
				name: category.node.name,
				translation: category.node.translation,
				description: JSON.parse(category.node.description) || null,
				descriptionJson: JSON.parse(category.node.descriptionJson) || null,
			};
		}
		if (category.node.children?.edges?.length) {
			const result = findCategoryDescription(category.node.children.edges, targetSlug);
			if (result) return result;
		}
	}
	return null;
};

// 递归查找分类路径（从根到当前分类）
const findCategoryPath = (categories: any[], targetSlug: string, path: any[] = []): any[] | null => {
	for (const category of categories) {
		const currentPath = [...path, {
			slug: category.node.slug,
			name: category.node.name,
			translation: category.node.translation,
		}];
		
		if (category.node.slug === targetSlug) {
			return currentPath;
		}
		
		if (category.node.children?.edges?.length) {
			const result = findCategoryPath(category.node.children.edges, targetSlug, currentPath);
			if (result) return result;
		}
	}
	return null;
};

function Index({ children, channel, locale, categoriesData }) {
	const t = useTranslations();
	const [open, setOpen] = useState(false);
	const [currentCategory, setCurrentCategory] = useState<any | null>(null);

	const onClose = () => {
		setOpen(false);
	};

	let isMobile = useIsMobile();

	let { changeBreakpointColumns, col, setcol } = useBreakpointColumns();

	function changeBreakpointColumnsObj(col: number) {
		changeBreakpointColumns(col);
		setcol(col);
	}
	let Pathname = usePathname();

	// 判断是否是一级分类页面（仅/products，没有slug）
	const isFirstLevelCategory = useMemo(() => {
		if (!Pathname) return false;
		const pathSegments = Pathname.split("/").filter(Boolean);
		// 如果路径是 /locale/products 或 /products，则是一级分类
		const productsIndex = pathSegments.indexOf('products');
		return productsIndex !== -1 && productsIndex === pathSegments.length - 1;
	}, [Pathname]);

	// 监听Pathname变化，获取当前分类的描述
	useEffect(() => {
		const loadCategoryData = async () => {
			if (Pathname && categoriesData?.edges) {
				// 从路径中提取分类slug
				const pathSegments = Pathname.split("/");
				const categorySlug = pathSegments[pathSegments.length - 1];
				const descriptionObj = await new Promise((resolve) => {
					setTimeout(() => {
						const result = findCategoryDescription(categoriesData.edges, categorySlug);
						resolve(result);
					}, 0);
				});

				// let jsondescription=description?JSON.parse(description):{}
				setCurrentCategory(descriptionObj);
			}
		};

		loadCategoryData();
	}, [Pathname, categoriesData]);

	const breadcrumbItems = useMemo(() => {
		const items = [
			{ label: t("nav.Home"), link: "/" },
			// 如果是一级分类页面，Product不添加link，使其显示为蓝色且不可点击
			{ label: t("menu.product"), link: isFirstLevelCategory ? undefined : "/product"},
		];

		// 如果不是一级分类页面，查找分类路径并添加到面包屑
		if (!isFirstLevelCategory && Pathname && categoriesData?.edges) {
			const pathSegments = Pathname.split("/").filter(Boolean);
			const productsIndex = pathSegments.indexOf('products');
			
			// 如果有分类slug
			if (productsIndex !== -1 && productsIndex < pathSegments.length - 1) {
				const categorySlug = pathSegments[pathSegments.length - 1];
				const categoryPath = findCategoryPath(categoriesData.edges, categorySlug);
				
				if (categoryPath && categoryPath.length > 0) {
					// 添加分类路径到面包屑，最后一个不添加link
					categoryPath.forEach((category, index) => {
						items.push({
							label: category.translation?.name || category.name,
							// 最后一个分类不添加link，使其显示为蓝色且不可点击
							link: index === categoryPath.length - 1 ? undefined : `/products/${category.slug}`,
						});
					});
				}
			}
		}

		return items;
	}, [Pathname, isFirstLevelCategory, categoriesData, t]);
	const bannerPageSlug = useMemo(() => {
		if (isFirstLevelCategory) {
			return "products";
		}

		const pathSegments = (Pathname || "").split("/").filter(Boolean);
		const currentSlug = pathSegments[pathSegments.length - 1];

		return currentSlug ? `products/${currentSlug}` : "products";
	}, [Pathname, isFirstLevelCategory]);

	return (
		<>
			<CommonTaile 
				isShowImage={false}
				msg={currentCategory?.translation?.name||currentCategory?.name || t("menu.product")
				}
				description={currentCategory?.description?.blocks[0]?.data?.text}
				backgroundImage="/image/plsyy/product-banner.webp"
				breadcrumbItems={breadcrumbItems}
				pageSlug={bannerPageSlug}
			/>

			<section className="blog list py-10 md:py-20">
				<div className="container mx-auto">
					<div className={`flex justify-between gap-y-12 ${!isFirstLevelCategory ? 'max-xl:flex-col' : ''}`}>
						{!isFirstLevelCategory && (
							<div className="right xl:w-1/4 xl:pr-[40px] space-y-8">
								<SearchBar channel={channel} />
								<Categories locale={locale} categoriesData={categoriesData} />
							</div>
						)}
						<div className={`flex flex-1 flex-col ${isFirstLevelCategory ? 'w-full' : ''}`}>{children}</div>
					</div>
				</div>
			</section>
			{!isFirstLevelCategory && (
				<Drawer
					title={t("common.Categories")}
					placement={"left"}
					onClose={onClose}
					open={open}
					key={"left"}
					width={isMobile ? "86vw" : "30vw"}
				>
					<SearchBar channel={channel} />
					<Categories locale={locale} categoriesData={categoriesData} />
				</Drawer>
			)}
		</>
	);
}

export default React.memo(Index);
